/*
  Once we test and mature this code a little more and add the functionality to pull plans from the API,
  we can evaluate what of this code should be made shared in the refresh-lib
*/
// START IMPORTS
// Payments imports
import { costPerReport, savings } from '@ltvco/refresh-lib/payments';
// Core lib v1 imports
import { type SubscriptionPlan } from '@ltvco/refresh-lib/v1';
// END IMPORTS

// START INTERFACES_AND_TYPES
export interface CurrentPlan {
  title: string;
  amount: number;
  /** Renewal period in months */
  renewalPeriod: number;
  /** Report limit */
  limit: number;
  costPerReport?: number;
}
// END INTERFACES_AND_TYPES

// START CONSTANTS
export const planTitles: {
  [key: number]: { title: string; description: string };
} = {
  20: {
    title: 'Saver',
    description: 'Save more per month by running fewer reports',
  },
  100: { title: 'Standard', description: 'Double your search capacity' },
  250: {
    title: 'Plus',
    description: 'More searches & lower your cost per report',
  },
  500: { title: 'Premium', description: 'Your premium reports package' },
  1000: { title: 'Premier +', description: 'Perfect for fast-tracking growth' },
  1500: { title: 'Premier +', description: 'Perfect for fast-tracking growth' },
  2500: {
    title: 'Enterprise',
    description: 'Perfect for fast-tracking growth',
  },
  3000: {
    title: 'Premier +',
    description: 'Perfect for fast-tracking growth',
  },
  5000: {
    title: 'Premier +',
    description: 'Perfect for fast-tracking growth',
  },
  7500: {
    title: 'Premier +',
    description: 'Perfect for fast-tracking growth',
  },
  10000: {
    title: 'Premier +',
    description: 'Perfect for fast-tracking growth',
  },
};

export const customPlanTitles: {
  [key: number]: { title: string; description: string; premier?: boolean };
} = {
  // Add custom plan titles here
};

const fallbackTitles: { [key: number]: { title: string } } = {
  20: { title: 'Saver' },
  50: { title: 'Starter' },
  100: { title: 'Lite User' },
  200: { title: 'Lite User' },
  400: { title: 'Plus User' },
  2000: { title: 'Elite User' },
};

const mostPopularPlanLimit = 20;
// END CONSTANTS

// START HELPER_FUNCTIONS
export function getPlanTitleAndDescription(
  planLimit: number,
  isCustomPlan: boolean = false
) {
  if (isCustomPlan && customPlanTitles[planLimit]) {
    return customPlanTitles[planLimit];
  }

  return (
    planTitles[planLimit] ||
    fallbackTitles[planLimit] || { title: 'Power User' }
  );
}

/** Returns the cost per report of the plan if it's not unlimited */
export function planCostPerReport(plan: {
  amount: number;
  renewalPeriod: number;
  limit: number;
}) {
  if (plan.limit === -1) {
    return;
  }

  return costPerReport(plan.amount, plan.renewalPeriod, plan.limit);
}

/** Returns the savings of the plan compared to the current plan */
export function planSavings(plan: SubscriptionPlan, currentPlan: CurrentPlan) {
  if (currentPlan.limit === -1 || plan.monthly_report_limit === -1) {
    return;
  }

  return savings(
    currentPlan.amount,
    currentPlan.renewalPeriod,
    currentPlan.limit,
    plan.amount / 100,
    plan.renewal_period,
    plan.monthly_report_limit
  );
}

export function getCustomPlan(
  brandPlan: SubscriptionPlan,
  currentPlan: CurrentPlan
) {
  return [mapPlan(brandPlan, currentPlan, false, true)];
}

/** Returns all the plans */
export function getPlans(
  brandPlans: SubscriptionPlan[],
  currentPlan: CurrentPlan,
  isDownsellVariation: boolean
) {
  let filteredPlans: SubscriptionPlan[];

  if (isDownsellVariation) {
    filteredPlans = brandPlans.filter(
      (plan) => plan.monthly_report_limit !== currentPlan.limit
    );
  } else {
    filteredPlans = brandPlans.filter(
      (plan) => plan.monthly_report_limit > currentPlan.limit
    );
  }

  return filteredPlans
    .map((plan) => mapPlan(plan, currentPlan, isDownsellVariation))
    .reverse();
}

export function mapPlan(
  plan: SubscriptionPlan,
  currentPlan: CurrentPlan,
  isDownsellVariation: boolean,
  isCustomPlan?: boolean
) {
  return {
    key: plan.unique_key,
    ...getPlanTitleAndDescription(plan.monthly_report_limit, isCustomPlan),
    amount: plan.amount / 100,
    limit: plan.monthly_report_limit,
    renewalPeriod: plan.renewal_period,
    renewalPeriodType: plan.renewal_period_type,
    costPerReport: planCostPerReport({
      amount: plan.amount / 100,
      renewalPeriod: plan.renewal_period,
      limit: plan.monthly_report_limit,
    }),
    savings: planSavings(plan, currentPlan),
    mostPopular: isDownsellVariation
      ? false
      : mostPopularPlanLimit === plan.monthly_report_limit,
  };
}

export function getBillingFrequency(renewalPeriod: number) {
  switch (renewalPeriod) {
    case 1:
      return 'monthly';
    case 3:
      return 'every 3 months';
    case 12:
      return 'annually';
    default:
      return 'N/A';
  }
}
// END HELPER_FUNCTIONS
